import React, { useRef, useEffect } from 'react';
import { useRecoilState } from 'recoil';
import { useTranslation } from 'react-i18next';
import { useClickAway } from 'react-use';
import { AnimatePresence, motion } from 'framer-motion';
import { alertAtom } from '../../atoms/Atoms';
import { backdropVariants, modalVariants } from '../../utils';
import { Spinner } from './Spinner';

function Alert() {
  const { t } = useTranslation();
  const ref = useRef();
  const [state, setState] = useRecoilState(alertAtom);

  useClickAway(ref, () => {
    setState({
      ...state,
      show: false,
    });
  });

  useEffect(() => {
    if (!state.show) {
      setState({
        ...state,
        title: '',
        content: '',
        onClick: null,
      });
    }
  }, [state.show]);

  return (
    <AnimatePresence>
      {state.show && (
        <motion.div
          variants={backdropVariants}
          initial="hidden"
          animate="visible"
          exit="hidden"
          className="fixed bg-gray-800 inset-0 bg-opacity-75 z-40 flex items-end md:items-center justify-center px-0 md:px-4"
        >
          <motion.div
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
            className="text-gray-800 antialiased justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none"
          >
            <div ref={ref} className="mx-auto max-w-md px-4">
              <div className="rounded-xl shadow-lg relative flex flex-col w-full bg-white p-4 space-y-4">
                <div className="text-center font-bold tracking-tight">
                  {state.title}
                </div>
                <div hidden={!state.loading} className="mx-auto">
                  <Spinner />
                </div>
                <div className="text-gray-500 text-sm text-center">
                  {state.content}
                </div>
                <div className="flex items-center justify-center">
                  <button
                    className="text-gray-400 disabled:cursor-not-allowed disabled:opacity-50 hover:text-gray-500 font-semibold px-4 text-sm outline-none focus:outline-none mr-1 mb-1 ease-linear transition-all duration-150"
                    type="button"
                    disabled={state.loading}
                    onClick={() => {
                      setState({
                        ...state,
                        show: false,
                      });
                    }}
                  >
                    {t('cancel')}
                  </button>
                  <button
                    className="flex items-center justify-between px-4 py-2 hover:shadow-md rounded-xl text-sm font-semibold tracking-tight text-white bg-accent hover:bg-accent disabled:cursor-not-allowed disabled:opacity-50 outline-none focus:outline-none transition duration-500 ease-in-out"
                    type="button"
                    disabled={state.loading}
                    onClick={state.onClick}
                  >
                    {t('accept')}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default Alert;
